package gr.tech.pdfgenerator.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.min
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorPalettes
import gr.tech.pdfgenerator.infrastructure.data.GeneratorData
import gr.tech.pdfgenerator.models.pdf.PdfDTO
import gr.tech.pdfgenerator.styles.*
import gr.tech.pdfgenerator.util.CONSTANTS.FONT_FAMILY2
import gr.tech.pdfgenerator.infrastructure.api.fetchPDFsByID
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.style.toAttrs
import gr.tech.pdfgenerator.util.CONSTANTS
import gr.tech.pdfgenerator.util.BACKEND
import gr.tech.pdfgenerator.infrastructure.api.deletePDFById
import gr.tech.pdfgenerator.util.formatDateAndTime
import gr.tech.pdfgenerator.infrastructure.pdf.parsePDFType
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

@Composable
fun ProjectDetailsModal(
    scope: CoroutineScope,
    onDismiss: () -> Unit,
) {
    val project = remember { GeneratorData.project.value }

    val pdfList = remember { mutableStateListOf<PdfDTO>() }
    var rowsToSkip by remember { mutableStateOf(0) }
    var showMoreVisibility by remember { mutableStateOf(false) }

    var showLoading by remember { mutableStateOf(true) }

    LaunchedEffect(Unit) {
        if (project.id != null) {
            fetchPDFsByID(
                id = project.id!!,
                skip = rowsToSkip,
                onSuccess = {
                    pdfList.clear()
                    pdfList.addAll(it)
                    rowsToSkip += CONSTANTS.ROWS_PER_FETCH_REQ
                    showMoreVisibility = it.size >= CONSTANTS.ROWS_PER_FETCH_REQ
                    showLoading = false
                },
                onError = {
                    showLoading = false
                    console.log(it)
                }
            )
        }
    }

    Box(
        modifier = Modifier
            .width(100.dvw)
            .height(100.dvh)
            .position(Position.Fixed)
            .top(0.px)
            .left(0.px)
            .zIndex(19)
            .overflow(Overflow.Hidden)
        ,
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .backgroundColor(Colors.White)
                .borderRadius(20.px)
                .margin(top = 4.cssRem)
                .padding(1.cssRem)
                .zIndex(20)
                .width(min(850.px, 100.percent - 2.cssRem))
                .maxHeight(600.px)
            ,
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Top
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth(),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically
            ) {
                FaX(
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .onClick {
                            onDismiss()
                        }
                        .color(Colors.Grey)
                )
            }

            H2(
                attrs = Modifier
                    .fontFamily(FONT_FAMILY2)
                    .fontSize(1.25.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .toAttrs()
            ) {
                Text("Project Details")
            }

            Row(
                modifier = Modifier
                    .fillMaxWidth(),
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {
                H3(
                    attrs = Modifier
                        .fontSize(1.25.cssRem)
                        .fontFamily(FONT_FAMILY2)
                        .fontWeight(FontWeight.Bold)
                        .toAttrs()
                ) {
                    if (GeneratorData.generalInputsState.value.projectName.isBlank()) {
                        Text("Untitled Project")
                    } else {
                        Text(GeneratorData.generalInputsState.value.projectName)
                    }
                }

                SpanText(
                    text = " - ID: ${project.id ?: "No ID"}",
                    modifier = Modifier
                        .fontSize(1.cssRem)
                        .fontFamily(FONT_FAMILY2)
                )
            }

            Row(
                modifier = Modifier
                    .fillMaxWidth(),
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {
                P(
                    attrs = Modifier
                        .fontFamily(FONT_FAMILY2)
                        .toAttrs()
                ) {
                    Text(project.description ?: "-")
                }
            }

            HorizontalDivider(Modifier.margin(topBottom = 8.px))

            Column(
                modifier = Modifier
                    .fillMaxWidth(),
                horizontalAlignment = Alignment.Start
            ) {
                P(
                    attrs = Modifier
                        .fontSize(1.cssRem)
                        .fontFamily(FONT_FAMILY2)
                        .fontWeight(FontWeight.Bold)
                        .toAttrs()
                ) {
                    Text("Created By: ")
                    SpanText(
                        text = project.createdBy ?: "NA",
                        modifier = Modifier
                            .fontWeight(FontWeight.Normal)
                    )
                }

                P(
                    attrs = Modifier
                        .fontSize(1.cssRem)
                        .fontFamily(FONT_FAMILY2)
                        .fontWeight(FontWeight.Bold)
                        .margin(bottom = 8.px)
                        .toAttrs()
                ) {
                    Text("Created At: ")
                    SpanText(
                        text = formatDateAndTime(project.createdAt) ?: "NA",
                        modifier = Modifier
                            .fontWeight(FontWeight.Normal)
                    )
                }

                P(
                    attrs = Modifier
                        .fontSize(1.cssRem)
                        .fontFamily(FONT_FAMILY2)
                        .fontWeight(FontWeight.Bold)
                        .toAttrs()
                ) {
                    Text("Updated By: ")
                    SpanText(
                        text = project.updatedBy ?: "NA",
                        modifier = Modifier
                            .fontWeight(FontWeight.Normal)
                    )
                }

                P(
                    attrs = Modifier
                        .fontSize(1.cssRem)
                        .fontFamily(FONT_FAMILY2)
                        .fontWeight(FontWeight.Bold)
                        .margin(bottom = 16.px)
                        .toAttrs()
                ) {
                    Text("Last Updated: ")
                    SpanText(
                        text = formatDateAndTime(project.updatedAt) ?: "NA",
                        modifier = Modifier
                            .fontWeight(FontWeight.Normal)
                    )
                }
            }

            H2(
                attrs = Modifier
                    .fontFamily(FONT_FAMILY2)
                    .fontSize(1.25.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .margin(bottom = 8.px)
                    .toAttrs()
            ) {
                Text("Saved PDFs")
            }

            // Table for Selecting Projects
            Column(
                modifier = Modifier
                    .height(300.px)
                    .overflow(overflowX = Overflow.Scroll, overflowY = Overflow.Auto)
                    .fillMaxWidth()
                    .borderRadius(5.px)
                    .border(1.px, LineStyle.Solid, Colors.DarkGray),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Table(
                    attrs = Modifier
                        .margin(0.px)
                        .fillMaxWidth()
                        .toAttrs()
                ) {

                    // Table Head and Headers
                    Thead(
                        attrs = Modifier
                            .position(Position.Sticky)
                            .top(0.px)
                            .zIndex(1)
                            .backgroundColor(Colors.White)
                            .toAttrs {
                                style {
                                    property("table-layout", "fixed")
                                }
                            }
                    ) {
                        Tr(
                            attrs = Modifier
                                .fillMaxWidth()
                                .toAttrs()
                        ) {
                            Th(
                                TableHeaderStyle.toModifier()
                                    .then(MediumColumnStyle).toAttrs()) {
                                Text("File")
                            }
                            Th(
                                TableHeaderStyle.toModifier()
                                    .width(150.px)
                                    .then(MediumColumnStyle).toAttrs()) {
                                Text("Type")
                            }
                            Th(
                                TableHeaderStyle.toModifier()
                                    .width(150.px)
                                    .then(MediumColumnStyle).toAttrs()) {
                                Text("Created")
                            }
                        }
                    }

                    // Table Body with Project Selecting rows
                    Tbody(
                        attrs = Modifier
                            .overflow(Overflow.Scroll)
                            .maxHeight(300.px)
                            .toAttrs()
                    ) {
                        pdfList.forEach { pdf ->
                            Tr(
                                attrs = Modifier
                                    .toAttrs()
                            ) {
                                Td(MediumColumnStyle
                                    .width(Width.FitContent)
                                    .toAttrs()
                                ) {
                                    FaFilePdf(
                                        modifier = Modifier
                                            .margin(right = 8.px)
                                            .color(Theme.MediumGreen.rgb),
                                        style = IconStyle.OUTLINE,
                                        size = IconSize.XL
                                    )
                                    A(
                                        href = "${BACKEND.BACKEND_URL}${BACKEND.ROUTES.PROJECT_PDF_DOWNLOAD}/${pdf.id}",
                                        attrs = MediumGreenHoverLink
                                            .toAttrs{
                                                attr("download", pdf.fileName ?: "file.pdf")
                                            }
                                    ) {
                                        Text(pdf.fileName ?: "-")
                                    }
                                }
                                Td(MediumColumnStyle
                                    .width(150.px)
                                    .toAttrs()
                                ) {
                                    pdf.projectType?.let {
                                        Text(
                                            parsePDFType(it)
                                        )
                                    }

                                }
                                Td(MediumColumnStyle
                                    .width(150.px)
                                    .toAttrs()) {
                                    Column {
                                        SpanText(text = formatDateAndTime(pdf.createdAt.toString()) ?: "-")
                                        SpanText(text = "${pdf.createdBy}")
                                    }
                                }
                                Td(
                                    SmallColumnStyle
                                    .toAttrs()) {
                                    Column {
                                        Button(
                                            attrs = Modifier
                                                .classNames("button")
                                                .margin(0.px)
                                                .padding(10.px)
                                                .border(1.px, LineStyle.Solid, Colors.Red)
                                                .backgroundColor(Colors.White)
                                                .onClick {
                                                    scope.launch {
                                                        if (pdf.id != null) {
                                                            pdfList.remove(pdf)
                                                            val response = deletePDFById(
                                                                id = pdf.id!!,
                                                                projectId = pdf.projectId!!,
                                                                onSuccess = {},
                                                                onError = {
                                                                    console.error("Unable to Delete PDF: ${pdf.id}")
                                                                }
                                                            )
                                                        }
                                                    }
                                                }
                                                .toAttrs()
                                        ) {
                                            FaTrash(
                                                modifier = Modifier
                                                    .color(Colors.Red),
                                                size = IconSize.SM
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // Show More Button
                Row(
                    modifier = Modifier
                        .margin(topBottom = 12.px)
                        .height(34.px),
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    com.varabyte.kobweb.silk.components.forms.Button(
                        colorPalette = ColorPalettes.Green,
                        size = ButtonSize.MD,
                        modifier = Modifier
                            .display(if (showMoreVisibility) DisplayStyle.Block else DisplayStyle.None)
                            .color(Colors.White),
                        onClick = {
                            scope.launch {
                                fetchPDFsByID(
                                    id = project.id!!,
                                    skip = rowsToSkip,
                                    onSuccess = {
                                        if (it.isNotEmpty()) {
                                            pdfList.addAll(it)
                                            rowsToSkip += CONSTANTS.ROWS_PER_FETCH_REQ
                                            showMoreVisibility = it.size >= CONSTANTS.ROWS_PER_FETCH_REQ
                                            if (it.size < CONSTANTS.ROWS_PER_FETCH_REQ) {
                                                showMoreVisibility = false
                                            }
                                        }  else {
                                            showMoreVisibility = false
                                        }
                                    },
                                    onError = {
                                        println(it)
                                    }
                                )
                            }
                        }
                    ) {
                        Text("Show More")
                    }
                }
            }

            Button(
                attrs = Modifier
                    .classNames("button")
                    .backgroundColor(Theme.MediumGreen.rgb)
                    .color(Colors.White)
                    .onClick {
                        onDismiss()
                    }
                    .toAttrs()
            ) {
                Text("Close")
            }
        }

        Box(
            modifier = Modifier
                .fillMaxSize()
                .backgroundColor(Theme.DarkerGray.rgb)
                .position(Position.Fixed)
                .padding(top = 5.cssRem)
                .onClick {
                    onDismiss()
                }
                .zIndex(19),
            contentAlignment = Alignment.TopCenter
        )
    }
}